import {
	Box,
	Button,
	Divider,
	Icons,
	Inline,
	Money,
	Stack,
	Text,
	noSpinnersClassName,
} from "@sembark-travel/ui-base"
import { Dialog, DialogBody } from "@sembark-travel/ui-dialog"
import {
	Form,
	ImageCropInputField,
	RadioInputField,
	SubmissionError,
	SwitchInputField,
	TextAreaInputField,
	TextInputField,
	useFieldValue,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import {
	addPercentageToMoney,
	moneyParseByDecimal,
} from "@sembark-travel/money"
import { acceptedImageTypes } from "../shared/utils"
import {
	emptyHotelAddOnFormValues,
	getTaxPercentage,
	hotelAddOnValidationSchema,
	TAX_PERCENTAGE_PRESETS,
} from "./utils"

export interface IHotelAddOnFormSchema {
	name: string
	// an emptied text input holds `undefined`, not ""
	description: string | undefined
	code: string | undefined
	rate_before_tax: string | undefined
	tax_type: "0" | "5" | "18" | "custom"
	custom_tax_percentage: string | undefined
	image?: File
	is_service_date_flexible: 1 | 0
}

function HotelAddOnForm({
	submitLabel,
	initialValues,
	initialImageUrl,
	currency,
	onSubmit,
}: {
	submitLabel: string
	// only passed when editing
	initialValues?: IHotelAddOnFormSchema
	initialImageUrl?: string
	currency: string
	onSubmit: (values: IHotelAddOnFormSchema) => Promise<void> | void
}) {
	const isEditing = Boolean(initialValues)
	return (
		<Form<IHotelAddOnFormSchema>
			initialValues={initialValues || emptyHotelAddOnFormValues}
			validate={validateFormValues(
				hotelAddOnValidationSchema({
					requireImage: !initialImageUrl,
					requireCode: isEditing,
				})
			)}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(values)
			})}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<Inline gap="8" padding="6" paddingBottom="0" flexWrap="wrap">
							<Stack gap="6" flex="1" style={{ minWidth: "20rem" }}>
								<TextInputField
									name="name"
									label="Name"
									type="text"
									placeholder="e.g. Airport Transfer"
								/>
								<TextAreaInputField
									name="description"
									label="Description"
									secondaryLabel="optional"
									placeholder="Type here"
									rows={3}
								/>
								<Inline gap="4" flexWrap="wrap">
									<TextInputField
										name="rate_before_tax"
										label="Base rate (before tax)"
										type="number"
										min={0}
										style={{ maxWidth: "15rem" }}
										className={noSpinnersClassName}
									/>
									<TextInputField
										name="code"
										label="Code"
										secondaryLabel={isEditing ? undefined : "optional"}
										type="text"
										placeholder="e.g. CLD01"
										maxLength={20}
										style={{ maxWidth: "12rem" }}
									/>
								</Inline>
								<TaxPercentageField />
								<TotalRatePreview currency={currency} />
								<SwitchInputField
									name="is_service_date_flexible"
									label="Subject to availability"
								/>
							</Stack>
							<ImageCropInputField
								preview
								crop={{ aspect: 1, minHeight: 100 }}
								name="image"
								label="Image"
								initialImageUrl={initialImageUrl}
								altText={initialValues?.name}
								accept={acceptedImageTypes.join(",")}
							/>
						</Inline>
						<Box paddingX="6">
							<SubmissionError />
						</Box>
						<Divider marginY="0" />
						<Inline padding="4" paddingTop="0" justifyContent="end">
							<Button type="submit" level="primary" disabled={submitting}>
								{submitting ? <Icons.Spinner /> : null}{" "}
								{submitting ? "Saving..." : submitLabel}
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function TaxPercentageField() {
	const { value: taxType } =
		useFieldValue<IHotelAddOnFormSchema["tax_type"]>("tax_type")
	return (
		<Stack gap="2">
			<Text fontWeight="semibold">Tax %</Text>
			<Inline gap="3" flexWrap="wrap" alignItems="start">
				{[...TAX_PERCENTAGE_PRESETS, "custom" as const].map((option) => (
					<Box
						key={option}
						paddingX="4"
						paddingY="2"
						borderWidth="1"
						borderRadius="md"
						borderColor={taxType === option ? "primary_emphasis" : "default"}
						bgColor={taxType === option ? "primary" : "default"}
					>
						<RadioInputField
							name="tax_type"
							value={option}
							label={option === "custom" ? "Custom" : `${option}%`}
							hideErrors
						/>
					</Box>
				))}
			</Inline>
			{taxType === "custom" ? (
				<TextInputField
					name="custom_tax_percentage"
					type="number"
					placeholder="e.g. 24%"
					min={0}
					max={100}
					style={{ maxWidth: "8rem" }}
					className={noSpinnersClassName}
				/>
			) : null}
		</Stack>
	)
}

function TotalRatePreview({ currency }: { currency: string }) {
	const { value: baseRate } =
		useFieldValue<IHotelAddOnFormSchema["rate_before_tax"]>("rate_before_tax")
	const { value: taxType } =
		useFieldValue<IHotelAddOnFormSchema["tax_type"]>("tax_type")
	const { value: customTaxPercentage } = useFieldValue<
		IHotelAddOnFormSchema["custom_tax_percentage"]
	>("custom_tax_percentage")
	const taxPercentage = getTaxPercentage({
		tax_type: taxType,
		custom_tax_percentage: customTaxPercentage,
	})
	if (
		!baseRate ||
		!Number.isFinite(Number(baseRate)) ||
		taxPercentage === undefined
	) {
		return (
			<Text color="muted">
				Total Rate (inc. Taxes) = &lt;Base rate&gt; + &lt;Tax %&gt;
			</Text>
		)
	}
	return (
		<Inline gap="1" alignItems="baseline" flexWrap="wrap">
			<Text color="muted">
				Total Rate (inc. Taxes) → {baseRate} + {taxPercentage}% =
			</Text>
			<Money
				money={addPercentageToMoney(
					moneyParseByDecimal(Number(baseRate), currency),
					taxPercentage
				)}
				showCurrency
				fontWeight="semibold"
			/>
		</Inline>
	)
}

/**
 * Add/Edit add-on in a dialog. Edits when `initialValues` is passed, otherwise adds.
 * Like the form, it only hands the values over to `onSuccess`.
 */
export function HotelAddOnInDialog({
	initialValues,
	initialImageUrl,
	currency,
	open,
	onClose,
	onSuccess,
}: Omit<
	React.ComponentProps<typeof HotelAddOnForm>,
	"submitLabel" | "onSubmit"
> & {
	onClose: () => void
	onSuccess: (values: IHotelAddOnFormSchema) => Promise<void> | void
	open: boolean
}) {
	return (
		<Dialog
			open={open}
			onClose={onClose}
			title={initialValues ? "Edit Extra Service" : "New Extra Service"}
			md
		>
			<DialogBody paddingX="0" paddingY="0">
				<HotelAddOnForm
					submitLabel={initialValues ? "Update" : "Add"}
					initialValues={initialValues}
					initialImageUrl={initialImageUrl}
					currency={currency}
					onSubmit={onSuccess}
				/>
			</DialogBody>
		</Dialog>
	)
}
